import { useEffect, type FunctionComponent } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { getMemberId, isMemberLoggedIn } from '@trello/authentication';
import { ErrorBoundary } from '@trello/error-boundaries';
import type { SentryErrorMetadata } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { Spinner } from '@trello/nachos/spinner';
import {
  dangerouslyConvertPrivacyString,
  type PIIString,
} from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import { getMemberActivityUrl, isMeAlias } from '@trello/urls';

import { LazyError } from 'app/src/components/Error/LazyError';
import { RuntimeError } from 'app/src/components/Error/RuntimeError';
import { MemberHeaderParent } from 'app/src/components/MemberHome/MemberHeaderParent';
import { SettingsFullPage } from 'app/src/components/SettingsFullPage';
import { WorkspacePageSpinner } from 'app/src/components/WorkspacePages/WorkspacePageSpinner';
import { MemberActivityView } from './MemberActivity/MemberActivityView';
import { getMemberAvatars } from './memberAvatarUtils';
import { useMemberPageQuery } from './MemberPageQuery.generated';
import { useMemberPageViewEvents } from './useMemberPageViewEvents';
import { useUsernameToQueryMemberId } from './useUsernameToQueryMemberId';

interface MemberActivityPageProps {
  username: PIIString;
}

const errorBoundaryTags: SentryErrorMetadata['tags'] = {
  ownershipArea: 'trello-billing-platform',
  feature: 'Members Activity Page',
};

export const MemberActivityPage: FunctionComponent<MemberActivityPageProps> = ({
  username,
}) => {
  const { startTask, taskSucceeded } = useMemberPageViewEvents({
    taskName: 'view-member/activity',
    source: 'memberActivityScreen',
  });

  const { value: isBillplatWorkspaceSettingsEnabled } = useFeatureGate(
    'billplat_workspace_settings_navigation',
  );

  useEffect(() => {
    startTask();

    Analytics.sendScreenEvent({ name: 'memberActivityScreen' });
  }, [startTask]);

  const { memberId: memberIdFromUsername, loading: usernameLoading } =
    useUsernameToQueryMemberId(username);
  const isMemberMe =
    isMemberLoggedIn() && memberIdFromUsername === getMemberId();

  const { data, loading, error } = useMemberPageQuery({
    waitOn: ['MemberHeader'],
    variables: { memberId: memberIdFromUsername ?? '' },
    skip: usernameLoading,
  });

  if (loading || usernameLoading) {
    return <Spinner centered />;
  }

  if (error || !data || !data?.member) {
    return <LazyError errorType={error ? 'serverError' : 'notFound'} />;
  }

  // Redirect to activity page with the username in the URL if using me alias or memberId
  if (
    isMeAlias(dangerouslyConvertPrivacyString(username)) ||
    memberIdFromUsername === dangerouslyConvertPrivacyString(username)
  ) {
    navigate(getMemberActivityUrl(data?.member?.username), {
      trigger: true,
      replace: true,
    });
    return;
  }

  const { member } = data ?? {};
  if (!member) {
    return null;
  }

  const avatars = getMemberAvatars(member);

  if (isBillplatWorkspaceSettingsEnabled) {
    return (
      <ErrorBoundary
        tags={errorBoundaryTags}
        errorHandlerComponent={RuntimeError}
      >
        <SettingsFullPage>
          {loading ? (
            <WorkspacePageSpinner />
          ) : error ? (
            <LazyError errorType={error ? 'serverError' : 'notFound'} />
          ) : (
            <MemberActivityView
              isMemberMe={isMemberMe}
              memberId={member.id}
              taskSucceeded={taskSucceeded}
            />
          )}
        </SettingsFullPage>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      tags={errorBoundaryTags}
      errorHandlerComponent={RuntimeError}
    >
      <MemberHeaderParent
        id={member.id}
        name={member.fullName as unknown as PIIString}
        username={username}
        initials={member.initials as unknown as PIIString}
        avatars={avatars}
        isMemberMe={isMemberMe}
        selected={'Activity'}
      />
      <MemberActivityView
        isMemberMe={isMemberMe}
        memberId={member.id}
        taskSucceeded={taskSucceeded}
      />
    </ErrorBoundary>
  );
};
